/**
 * @fileoverview React error boundary.
 * @module components/common/error-boundary
 * @description
 * Mirrors the Angular `GlobalErrorHandler`, which logged uncaught errors via
 * `console.error('Global error:', error)`. Catches render-phase errors in the
 * subtree, logs them, and renders a theme-aware fallback UI built from existing
 * CSS custom properties (`--bg-primary`, `--text-primary`).
 *
 * @author Liraz Amir
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. When omitted, a default centered message renders. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Mirror Angular GlobalErrorHandler: console.error('Global error:', error).
    console.error('Global error:', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }

      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2rem',
            textAlign: 'center',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
          }}
        >
          <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>Something went wrong</h1>
          <p style={{ margin: 0, opacity: 0.8 }}>
            An unexpected error occurred. Please refresh the page to continue.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
